import React, {
  ChangeEvent,
  ChangeEventHandler,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, FormControl, FormLabel, Input, Select } from '@chakra-ui/react';

import { useGlobalContext } from '../../subscriptions/state/GlobalContext';

interface Props {
  values: ModalFormAddress;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
}

export default ({ values, handleChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const { countries } = useGlobalContext();
  const [provinces, setProvinces] = useState<Province[] | undefined>();

  useEffect(() => {
    if (countries?.length) {
      handleChange({
        target: {
          name: 'country',
          value: countries[0].name,
        },
      } as ChangeEvent<HTMLInputElement>);
    }
  }, []);

  useEffect(() => {
    if (values.country) {
      const countryProvinces: Province[] | undefined = countries?.find(
        (c) => c.name == values.country,
      )?.provinces;

      if (countryProvinces && countryProvinces.length) {
        setProvinces(countryProvinces);

        let selectedProvince = countryProvinces[0][0];

        if (
          values.province &&
          countryProvinces.map((p) => p[0]).includes(values.province)
        ) {
          selectedProvince = values.province;
        }

        handleChange({
          target: {
            name: 'province',
            value: selectedProvince,
          },
        } as ChangeEvent<HTMLInputElement>);
      } else {
        setProvinces(undefined);
        handleChange({
          target: {
            name: 'province',
            value: '',
          },
        } as ChangeEvent<HTMLInputElement>);
      }
    }
  }, [values.country]);

  return (
    <>
      <Flex className="firstName" mb="6">
        <FormControl id="firstName">
          <Input
            name="firstName"
            onChange={handleChange}
            placeholder={t('new_payment_method.first_name')}
            required
            type="text"
            value={values.firstName}
          />
          <FormLabel>{t('new_payment_method.first_name')}</FormLabel>
        </FormControl>
      </Flex>

      <Flex className="lastName" mb="6">
        <FormControl id="lastName">
          <Input
            name="lastName"
            onChange={handleChange}
            placeholder={t('new_payment_method.last_name')}
            required
            type="text"
            value={values.lastName}
          />
          <FormLabel>{t('new_payment_method.last_name')}</FormLabel>
        </FormControl>
      </Flex>

      <Flex className="address1" mb="6">
        <FormControl id="address1">
          <Input
            name="address1"
            onChange={handleChange}
            placeholder={t('new_payment_method.address1')}
            required
            type="text"
            value={values.address1}
          />
          <FormLabel>{t('new_payment_method.address1')}</FormLabel>
        </FormControl>
      </Flex>

      <Flex className="address2" mb="6">
        <FormControl id="address2">
          <Input
            name="address2"
            onChange={handleChange}
            placeholder={t('new_payment_method.address2')}
            type="text"
            value={values.address2}
          />
          <FormLabel>{t('new_payment_method.address2')}</FormLabel>
        </FormControl>
      </Flex>

      <Flex className="city" mb="6">
        <FormControl id="city">
          <Input
            name="city"
            onChange={handleChange}
            placeholder={t('new_payment_method.city')}
            required
            type="text"
            value={values.city}
          />
          <FormLabel>{t('new_payment_method.city')}</FormLabel>
        </FormControl>
      </Flex>

      <Flex className="country" mb="6">
        <FormControl id="country">
          <Select
            name="country"
            onChange={handleChange}
            placeholder={t('new_payment_method.country')}
            required
            value={values.country}
          >
            {countries?.map((c, idx) => (
              <option key={idx} value={c.name}>
                {c.name}
              </option>
            ))}
          </Select>
          <FormLabel>{t('new_payment_method.country')}</FormLabel>
        </FormControl>
      </Flex>

      {provinces?.length ? (
        <Flex className="province" mb="6">
          <FormControl id="province">
            <Select
              name="province"
              onChange={handleChange}
              placeholder={t('new_payment_method.state')}
              value={values.province}
            >
              {provinces?.map((p) => (
                <option key={p[0]} value={p[0]}>
                  {p[0]}
                </option>
              ))}
            </Select>
            <FormLabel>{t('new_payment_method.state')}</FormLabel>
          </FormControl>
        </Flex>
      ) : null}

      <Flex className="zip" mb="6">
        <FormControl id="zip">
          <Input
            name="zip"
            onChange={handleChange}
            placeholder={t('new_payment_method.zip')}
            required
            type="text"
            value={values.zip}
          />
          <FormLabel>{t('new_payment_method.zip')}</FormLabel>
        </FormControl>
      </Flex>

      <Flex className="phone">
        <FormControl id="phone">
          <Input
            name="phone"
            onChange={handleChange}
            placeholder={t('new_payment_method.phone')}
            type="text"
            value={values.phone}
          />
          <FormLabel>{t('new_payment_method.phone')}</FormLabel>
        </FormControl>
      </Flex>
    </>
  );
};
